import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface BusinessSectorSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Business Sector select component.
 * Fetches business sectors from the 'businessSector' provider.
 *
 * Used for parameter type: BUSINESS_SECTOR_SELECT
 */
const BusinessSectorSelect: React.FC<BusinessSectorSelectProps> = ({
  disabled,
  placeholder,
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    search,
    fieldName: 'businessSector',
    entityType: 'REPORT',
  }), [search]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('businessSector', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select business sector'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load business sectors' : isLoading ? 'Loading...' : 'No business sectors'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default BusinessSectorSelect;
